<script lang="ts">
    import {KpPageContainer} from 'shared/layouts';
    import type {PagedParameteredResult} from 'src/typings';
    import {states} from 'shared/constants';
    import {getInjector, getLocalization} from 'src/core';
    import type {RecordCollectionUpdateType} from '../../record-collections/record-collection-manager';
    import type {SearchManager} from '../search-manager/search-manager';
    import type {Subscription} from 'rxjs';
    import {SearchPageService} from './search-page.service';
    import {onDestroy, onMount} from 'svelte';
    import {isDocument} from '../kp-resolvable-search-results-displayer/search-result-resolver';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import KpSearchHeader from '../kp-search-header/KpSearchHeader.svelte';
    import KpSearchResultInfo from '../kp-search-result-info/KpSearchResultInfo.svelte';
    import KpFavouritesPanel from '../../favourites/kp-favourites-panel/KpFavouritesPanel.svelte';
    import KpVisitedPanel from '../../record/kp-visited-documents-panel/KpVisitedPanel.svelte';
    import KpSearchAlphabets from '../kp-search-alphabets/KpSearchAlphabets.svelte';
    import KpSearchFooter from '../kp-search-footer/KpSearchFooter.svelte';
    import KpSearchSideContent from '../kp-search-content-layouts/KpSearchSideContent.svelte';
    import KpSearchMainContent from '../kp-search-content-layouts/KpSearchMainContent.svelte';
    import KpSearchResultsExportButton from '../kp-search-footer/KpSearchResultsExportButton.svelte';
    import KpVerticalSeparator from 'shared/ui-widgets/separator/KpVerticalSeparator.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';

    export let searchManager: SearchManager<any>;

    const localize = getLocalization();
    const service = getInjector().getByClass(SearchPageService);

    $: lastSearch = searchManager.getLastSearch$();

    let loadingItems = true;
    let initialLoadComplete = false;

    let favouritesInsertsSubscription: Subscription;
    let searchesSubscription: Subscription;
    let paginationDataSubscription: Subscription;
    let asideElement: HTMLElement;

    onMount(async () => {
        searchesSubscription = searchManager.getState$().subscribe((state) => {
            if (exists(state.pageLoadInProgress)) {
                service.clearPotentials();
            }
        });

        paginationDataSubscription = searchManager.getPagination().getPaginationData$().subscribe((data) => {
            if (initialLoadComplete) {
                return;
            }

            loadingItems = data.state.distantPage === states.PROCESSING;

            if (data.state.distantPage === states.NORMAL && exists($lastSearch)) {
                initialLoadComplete = true;
            }
        });

        const favouritesCollectionManager = await service.getFavouritesCollectionManager();
        favouritesInsertsSubscription = favouritesCollectionManager
            .getCollectionUpdateEvent$()
            .subscribe((event) => scrollToFavouritesPanelAfterFirstInsert(event.type, favouritesCollectionManager.getSize()));
    });

    $: handleLastSearchResults($lastSearch?.result);

    onDestroy(() => {
        service.clearPotentials();
        cleanup(favouritesInsertsSubscription, searchesSubscription, paginationDataSubscription);
    });

    function scrollToFavouritesPanelAfterFirstInsert(type: RecordCollectionUpdateType, size: number) {
        if (type === 'insert' && size === 1) {
            asideElement?.querySelector('#favourites-panel')?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }

    function handleLastSearchResults(searchResult: PagedParameteredResult<any, any> | undefined) {
        if (exists(searchResult)) {
            service.registerPotentials(searchResult.content.filter(isDocument));
        }
    }
</script>

<KpPageContainer gap="0px" additionalClasses="kp-full-page-search search type-{$lastSearch?.type ?? ''}">
    {#if !initialLoadComplete && loadingItems}
        <KpLoadingBlock fillAvailableSpace size="md">
            <span class="text-muted">{localize(/* @kp-localization commons.LoadingInProgress */ 'commons.LoadingInProgress')}</span>
        </KpLoadingBlock>
    {:else}
        <div class="row">
            <div class="main-panel-hlavni col-sm-8">
                <KpSearchHeader {searchManager}/>
                <KpSearchAlphabets {searchManager}/>
                <KpSearchResultInfo {searchManager}/>
                <KpSearchMainContent {searchManager}/>
            </div>

            <aside class="main-panel-pravy side-panels col-sm-4" bind:this={asideElement}>
                <KpSearchSideContent {searchManager}/>
                <KpFavouritesPanel/>
                <KpVisitedPanel/>
            </aside>
        </div>

        <KpSearchFooter {searchManager}>
            {#if $lastSearch.availableExports.length > 0}
                <KpSearchResultsExportButton availableExports={$lastSearch.availableExports}/>
                <KpVerticalSeparator height="16px"/>
            {/if}
            <a href="/search-history">{localize(/* @kp-localization hledani.ZobrazitHledaneDotazy */ 'hledani.ZobrazitHledaneDotazy')}</a>
        </KpSearchFooter>
    {/if}
</KpPageContainer>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .side-panels {
        display: flex;
        flex-direction: column;
        gap: @spacing-ml;
    }

    :global {
        .kp-full-page-search .side-panels {
            & > :empty {
                display: none;
            }

            .panel {
                margin-bottom: 0;
            }
        }
    }
</style>